import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { setSubjectFilter } from '@state/actions/filtersActions'
import { moveItemInArray } from '@helpers/utilities'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  DIVIDER_SIZE,
  HEADING_TYPE,
  TEXT_SIZES,
  PANELS,
} from '@helpers/constants'
import Button from '@components/Button'
import Icon from '@components/Icon'
import Text from '@components/Text'
import Loader from '@components/Loader'

import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { useUpdateBookSectionOrder } from '@hooks/mutations/useUpdateBookSectionOrder'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import { useSingleBook } from '@hooks/useSingleBook'

const EditSections = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { currentEditBookId },
  } = state

  const { book, error, isLoading } = useSingleBook(currentEditBookId)
  const { id: bookId, sections, subject } = book

  const [updatingSectionId, setUpdatingSectionId] = useState(null)

  const { post: postOrder, isPosting: isPostingOrder } =
    useUpdateBookSectionOrder()
  const { post: postUpdate, isPosting: isPostingUpdate } = useUpdateBook()

  const moveSection = async (sectionId, pivotId, direction) => {
    const allPivotIds = sections.map((section) => section.pivot.id)
    const currentPosition = allPivotIds.indexOf(pivotId)

    if (
      (direction === 'up' && currentPosition === 0) ||
      (direction === 'down' && currentPosition === allPivotIds.length - 1)
    ) {
      // Cannot move first item up or last item down
    } else {
      const newPosition =
        direction === 'up' ? currentPosition - 1 : currentPosition + 1
      const reorderedPivotIds = moveItemInArray(
        allPivotIds,
        currentPosition,
        newPosition
      )

      // post new order
      setUpdatingSectionId(sectionId)
      const res = await postOrder({
        id: bookId,
        ids: reorderedPivotIds,
      })
    }
  }

  const removeSection = async (sectionId) => {
    setUpdatingSectionId(sectionId)
    const res = await postUpdate(
      {
        id: bookId,
        disconnectSections: [sectionId],
      },
      'Avsnitt borttaget.',
      'Nåt gick fel när avsnittet skulle tas bort.'
    )
  }

  return (
    <>
      {sections?.length > 0 ? (
        <div className={'flex flex-col items-start -mx-8'}>
          {sections.map((section, index) => {
            const {
              id,
              name,
              pivot: { id: pivotId },
            } = section

            const rowClasses = classNames(
              'flex items-center justify-between py-2 border-mediumGrey border-b',
              {
                'border-t': index === 0,
              }
            )

            return (
              <div
                key={id}
                className={`hover:bg-lightGrey hover:text-subject-${subject.slug}-light text-mediumGrey px-8 w-full`}
              >
                <div className={rowClasses}>
                  <div className={'flex items-center'}>
                    <span className={'mr-2'}>
                      <Icon icon={'Section'} />
                    </span>
                    <Text>{name}</Text>
                  </div>

                  <div className={'flex items-center'}>
                    {updatingSectionId === id && isPostingOrder && (
                      <Loader noMargin />
                    )}

                    <div className={'mr-2'}>
                      <ButtonWithLoader
                        type={BUTTON_TYPE.PRIMARY}
                        isLoading={updatingSectionId === id && isPostingUpdate}
                        disabledText={'Ta bort'}
                        enabledText={'Ta bort'}
                        disabled={updatingSectionId === id && isPostingUpdate}
                        size={BUTTON_SIZE.MEDIUM}
                        handleClick={() => {
                          removeSection(id)
                        }}
                      />
                    </div>
                    <Button
                      icon={'ChevronUp'}
                      size={'noPadding'}
                      textColor={COLORS.BLACK.TEXT}
                      handleClick={() => moveSection(id, pivotId, 'up')}
                    />
                    <Button
                      icon={'ChevronDown'}
                      size={'noPadding'}
                      textColor={COLORS.BLACK.TEXT}
                      handleClick={() => moveSection(id, pivotId, 'down')}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className='mx-auto mt-[25%] h-full max-w-[260px] text-center'>
          <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.LARGE}>
            Välj avsnitt att lägga till i din bok
          </Heading>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Button
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            iconLeft
            icon={'Section'}
            to={'/bibliotek/avsnitt'}
            handleClick={() => {
              dispatch(setSubjectFilter(subject.id))
              dispatch(closePanel(PANELS.EDIT_BOOK))
            }}
          >
            Välj avsnitt
          </Button>
        </div>
      )}
    </>
  )
}

export default EditSections
